import { ArrowsClockwise, LinkBreak, PlugsConnected } from '@phosphor-icons/react';
import { Button, Checkbox, ChoiceGroup, Heading, Icon, Text, TextField } from '@shh/ui';
import { useState } from 'react';
import {
  bringChooseList,
  bringConnect,
  bringDisconnect,
  addToList,
  bringSync,
  friendlyError,
} from '../../data/api';
import { useFamily } from '../../data/family';
import { settingsText } from '../../i18n/settings';

/**
 * Settings → Einkaufsliste: the family's own list is the default (1.20); a family that already
 * uses Bring! can connect it instead. The password is sent once to sign in and is not stored –
 * the server keeps only Bring!'s sign-in tokens.
 */
export function ShoppingSettings() {
  const f = useFamily();
  const list = f.shopping;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [done, setDone] = useState<string>();
  const [takeOver, setTakeOver] = useState(true);

  const run = async (what: () => Promise<unknown>, ok?: string) => {
    setBusy(true);
    setError(undefined);
    setDone(undefined);
    try {
      await what();
      if (ok) setDone(ok);
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setBusy(false);
    }
  };

  const feedback = (
    <>
      {error && (
        <p role="alert" className="font-semibold text-feedback-danger">
          {error}
        </p>
      )}
      {done && (
        <p role="status" className="font-semibold text-feedback-success">
          {done}
        </p>
      )}
    </>
  );

  if (!list || list.error === 'auth') {
    return (
      <div className="flex flex-col gap-gap-lg">
        {list ? (
          <Text>{settingsText.bringAuth}</Text>
        ) : (
          <>
            <Text>{settingsText.ownList}</Text>
            <Heading level={3} size="sm">
              {settingsText.bringInstead}
            </Heading>
            <Text>{settingsText.bringIntro}</Text>
          </>
        )}
        {/* Bring!'s sign-in for apps like ours needs e-mail + password (no Google/Apple login) */}
        <section
          aria-labelledby="bring-google"
          className="flex flex-col gap-gap-xs rounded-card bg-surface-sunken p-inset-sm text-sm"
        >
          <h3 id="bring-google" className="font-semibold">
            {settingsText.bringGoogle}
          </h3>
          <p>
            {settingsText.bringPassword1} <strong>{settingsText.bringPath}</strong>{' '}
            {settingsText.bringOr}{' '}
            <a
              href="https://web.getbring.com/app/settings/resetpassword"
              target="_blank"
              rel="noreferrer"
              className="font-semibold text-text-link underline"
            >
              web.getbring.com
            </a>
            {settingsText.bringPassword2}
          </p>
        </section>
        <ConnectForm busy={busy} onConnect={(email, pw) => run(() => bringConnect(email, pw))} />
        {feedback}
        <Text size="sm" tone="muted">
          {settingsText.bringPrivacy}
        </Text>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-gap-lg">
      <p className="flex items-center gap-gap-sm font-semibold">
        <Icon icon={PlugsConnected} size="md" className="shrink-0 text-feedback-success" />
        {settingsText.connected(list.listName, list.purchase.length)}
      </p>
      {list.lists.length > 1 && (
        <ChoiceGroup
          label={settingsText.whichList}
          value={list.listUuid}
          onChange={(uuid) => void run(() => bringChooseList(uuid), settingsText.listChanged)}
          choices={list.lists.map((l) => ({ value: l.uuid, label: l.name }))}
        />
      )}
      {list.purchase.length > 0 && (
        <Checkbox isSelected={takeOver} onChange={setTakeOver}>
          {settingsText.takeOver}
        </Checkbox>
      )}
      <div className="flex flex-wrap gap-gap-sm">
        <Button
          variant="secondary"
          icon={ArrowsClockwise}
          isDisabled={busy}
          onPress={() => void run(() => bringSync(), settingsText.synced)}
        >
          {settingsText.syncNow}
        </Button>
        <Button
          variant="quiet"
          icon={LinkBreak}
          isDisabled={busy}
          onPress={() =>
            void run(async () => {
              // what is still on the Bring! list moves over, so nothing is forgotten
              if (takeOver) {
                await Promise.all(
                  list.purchase.map((i) =>
                    addToList(
                      f.householdId,
                      f.listItems,
                      { name: i.name, spec: i.spec },
                      f.meId ?? null,
                    ),
                  ),
                );
              }
              await bringDisconnect();
            }, settingsText.disconnected)
          }
        >
          {settingsText.disconnect}
        </Button>
      </div>
      {feedback}
    </div>
  );
}

function ConnectForm({
  busy,
  onConnect,
}: {
  busy: boolean;
  onConnect: (email: string, password: string) => Promise<unknown>;
}) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  return (
    <form
      className="flex flex-col gap-gap-md"
      onSubmit={(e) => {
        e.preventDefault();
        void onConnect(email.trim(), password).then(() => setPassword(''));
      }}
    >
      <TextField
        label={settingsText.bringEmail}
        type="email"
        autoComplete="username"
        value={email}
        onChange={setEmail}
      />
      <TextField
        label={settingsText.bringPw}
        type="password"
        autoComplete="current-password"
        value={password}
        onChange={setPassword}
      />
      <Button
        type="submit"
        icon={PlugsConnected}
        className="self-start"
        isDisabled={busy || !email.includes('@') || !password}
      >
        {busy ? settingsText.connecting : settingsText.connect}
      </Button>
    </form>
  );
}
